import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Check, 
  Crown,
  ArrowLeft,
  Mail,
  Lock,
  User,
  Loader2,
  Eye,
  EyeOff,
  Sparkles,
  X
} from 'lucide-react';
import { useSafeMode } from '../contexts/SafeModeContext';
import { usePricingGrid } from '../hooks/usePricingGrid';
import { useOAuthConfig } from '../hooks/useOAuthConfig';
import { toast } from 'sonner';
import api from '../services/api';
import { initSiteAnalytics, trackClick } from '../utils/siteAnalytics';

const getPasswordStrength = (password) => {
  if (!password) return { score: 0, label: '', color: '' };
  
  let score = 0;
  const checks = {
    length: password.length >= 8,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };

  if (checks.length) score++;
  if (checks.lowercase) score++;
  if (checks.uppercase) score++;
  if (checks.number) score++;
  if (checks.special) score++;

  const labels = {
    0: { label: '', color: '' },
    1: { label: 'Very Weak', color: 'bg-red-500' },
    2: { label: 'Weak', color: 'bg-orange-500' },
    3: { label: 'Fair', color: 'bg-yellow-500' },
    4: { label: 'Strong', color: 'bg-green-500' },
    5: { label: 'Very Strong', color: 'bg-green-600' },
  };

  return { score, checks, ...labels[score] };
};

const SignupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [authMethod, setAuthMethod] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const { safeMode } = useSafeMode();
  const { getOAuthUrl, isCustomOAuth } = useOAuthConfig();
  const { getSignupPricingInfo, getPlan, getFreePlan } = usePricingGrid();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  const passwordStrength = getPasswordStrength(formData.password);
  
  const selectedPlanId = location.state?.selectedPlanId;
  const selectedPlan = selectedPlanId ? getPlan(selectedPlanId) : null;
  
  const isPaidPlan = selectedPlan?.plan_type === 'paid';
  const isInquirePlan = selectedPlan?.plan_type === 'inquire';
  const trialDays = selectedPlan?.trial_days || 14;

  useEffect(() => {
    const cleanup = initSiteAnalytics();
    return () => { if (cleanup) cleanup(); };
  }, []);
  
  useEffect(() => {
    if (!selectedPlanId) {
      toast.error('Please select a plan first');
      navigate('/pricing', { replace: true });
    }
  }, [selectedPlanId, navigate]);
  
  useEffect(() => {
    if (isInquirePlan && selectedPlan?.inquire_url) {
      window.open(selectedPlan.inquire_url, '_blank');
      navigate('/');
    }
  }, [isInquirePlan, selectedPlan, navigate]);
  
  if (!selectedPlanId || !selectedPlan) {
    return null;
  }

  const getPricingData = () => {
    const pricingInfo = getSignupPricingInfo();
    return {
      variant: pricingInfo.variant,
      version_id: pricingInfo.version_id,
      plan_id: selectedPlan.plan_id,
      plan_type: selectedPlan.plan_type,
      plan_label: selectedPlan.label,
      trial_days: selectedPlan.trial_days || 0,
      price_cents: selectedPlan.price_cents || 0
    };
  };

  const handleGoogleSignup = () => {
    setLoading(true);
    
    const pricingData = getPricingData();
    
    sessionStorage.setItem('is_new_signup', 'true');
    sessionStorage.setItem('signup_safe_mode', JSON.stringify(safeMode));
    sessionStorage.setItem('signup_pricing', JSON.stringify(pricingData));
    
    const callbackPath = isCustomOAuth() ? '/auth/google/callback' : '/auth/callback';
    const redirectUrl = window.location.origin + callbackPath;
    
    if (isCustomOAuth()) {
      window.location.href = getOAuthUrl(redirectUrl, pricingData);
    } else {
      window.location.href = getOAuthUrl(redirectUrl);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) errors.name = 'Name is required';
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (passwordStrength.score < 3) {
      errors.password = 'Please choose a stronger password';
    }
    
    if (!agreedToTerms) {
      errors.terms = 'You must agree to the Terms of Service and Privacy Policy';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const response = await api.post('/api/auth/register', {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        signup_pricing: getPricingData()
      });
      
      const data = response.data;
      
      sessionStorage.setItem('is_new_signup', 'true');
      sessionStorage.setItem('signup_safe_mode', JSON.stringify(safeMode));
      
      if (data.checkout_url) {
        toast.success('Account created! Redirecting to payment setup...');
        window.location.href = data.checkout_url;
        return;
      }
      
      toast.success('Account created successfully!');
      navigate('/onboarding', {
        replace: true,
        state: { 
          user: { user_id: data.user_id, email: data.email, name: data.name }, 
          organization: { organization_id: data.organization_id } 
        }
      });
      
    } catch (err) {
      console.error('Registration error:', err);
      const message = err.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const features = isPaidPlan ? [
    `Full access to ${selectedPlan?.label} features`,
    `${trialDays}-day free trial`,
    'Credit card required to start trial',
    'Cancel anytime before trial ends'
  ] : [
    'Up to 25 active requirements',
    'AI-powered product breakdown',
    'Automatic persona generation',
    'No credit card required'
  ];

  return (
    <div className="min-h-screen bg-[#030303] text-white font-sans selection:bg-purple-500 selection:text-white flex flex-col" data-testid="signup-page">

      {/* Nav — matching homepage */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-black/80 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
              <span className="text-white font-bold text-sm">SR</span>
            </div>
            <span className="text-lg font-semibold text-white tracking-tight group-hover:text-gray-300 transition-colors">
              StackRanked.ai
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-gray-400 hover:text-white hover:bg-white/5 gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Home</span>
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/login')}
              className="text-gray-400 hover:text-white hover:bg-white/5"
            >
              Sign In
            </Button>
          </div>
        </div>
      </nav>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center pt-24 pb-16 px-4">
        <div className="w-full max-w-md">

          {/* Card */}
          <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-8 backdrop-blur-sm">

            {/* Plan Context Header */}
            <div className="text-center mb-8">
              {isPaidPlan ? (
                <>
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 bg-purple-500/10 border border-purple-500/20 text-purple-300">
                    <Crown className="w-4 h-4" />
                    <span className="font-medium text-sm">{selectedPlan?.label} Plan</span>
                    <span className="text-xs opacity-75">&#183; {trialDays}-day free trial</span>
                  </div>
                  <h1 className="text-2xl font-bold mb-2 text-white">Start your free trial</h1>
                  <p className="text-sm text-gray-500">Create your account to begin</p>
                </>
              ) : (
                <>
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 bg-purple-500/10 border border-purple-500/20 text-purple-300">
                    <Sparkles className="w-4 h-4" />
                    <span className="font-medium text-sm">Free Plan</span>
                  </div>
                  <h1 className="text-2xl font-bold mb-2 text-white">Create your account</h1>
                  <p className="text-sm text-gray-500">Get started in seconds</p>
                </>
              )}
            </div>

            {/* Auth Method Selection */}
            {!authMethod && (
              <div className="space-y-3">
                {/* Google Option */}
                <button
                  onClick={handleGoogleSignup}
                  disabled={loading}
                  data-testid="signup-google-button"
                  className="w-full p-4 rounded-xl border border-white/10 bg-white/[0.04] flex items-center gap-4 transition-all hover:bg-white/[0.08] hover:border-purple-500/30"
                >
                  <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  </div>
                  <div className="text-left flex-1">
                    <p className="font-medium text-white">Continue with Google</p>
                    <p className="text-xs text-gray-500">Fastest way to get started</p>
                  </div>
                </button>

                {/* Email Option */}
                <button
                  onClick={() => setAuthMethod('email')}
                  disabled={loading}
                  data-testid="signup-email-button"
                  className="w-full p-4 rounded-xl border border-white/10 bg-white/[0.04] flex items-center gap-4 transition-all hover:bg-white/[0.08] hover:border-purple-500/30"
                >
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5 text-gray-300" />
                  </div>
                  <div className="text-left flex-1">
                    <p className="font-medium text-white">Continue with Email</p>
                    <p className="text-xs text-gray-500">Use your email and password</p>
                  </div>
                </button>
              </div>
            )}

            {/* Email Form */}
            {authMethod === 'email' && (
              <form onSubmit={handleEmailSignup} className="space-y-4">
                <button
                  type="button"
                  onClick={() => setAuthMethod(null)}
                  className="flex items-center gap-1 text-sm mb-2 text-gray-500 hover:text-gray-300 transition-colors"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Other sign up options
                </button>

                {/* Name */}
                <div>
                  <Label htmlFor="name" className="text-gray-300 text-sm">Full Name</Label>
                  <div className="relative mt-1.5">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className={`pl-10 bg-white/[0.04] border-white/10 text-white placeholder:text-gray-600 focus:border-purple-500/40 focus:ring-purple-500/20 ${formErrors.name ? 'border-red-500' : ''}`}
                      disabled={loading}
                      data-testid="signup-name-input"
                    />
                  </div>
                  {formErrors.name && <p className="text-red-400 text-xs mt-1">{formErrors.name}</p>}
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email" className="text-gray-300 text-sm">Email Address</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className={`pl-10 bg-white/[0.04] border-white/10 text-white placeholder:text-gray-600 focus:border-purple-500/40 focus:ring-purple-500/20 ${formErrors.email ? 'border-red-500' : ''}`}
                      disabled={loading}
                      data-testid="signup-email-input"
                    />
                  </div>
                  {formErrors.email && <p className="text-red-400 text-xs mt-1">{formErrors.email}</p>}
                </div>

                {/* Password */}
                <div>
                  <Label htmlFor="password" className="text-gray-300 text-sm">Password</Label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min. 8 characters"
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      className={`pl-10 pr-10 bg-white/[0.04] border-white/10 text-white placeholder:text-gray-600 focus:border-purple-500/40 focus:ring-purple-500/20 ${formErrors.password ? 'border-red-500' : ''}`}
                      disabled={loading}
                      data-testid="signup-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  
                  {formData.password && (
                    <div className="mt-2">
                      <div className="flex gap-1 mb-1">
                        {[1, 2, 3, 4, 5].map((level) => (
                          <div
                            key={level}
                            className={`h-1 flex-1 rounded-full transition-colors ${
                              level <= passwordStrength.score ? passwordStrength.color : 'bg-white/10'
                            }`}
                          />
                        ))}
                      </div>
                      <div className="flex justify-between items-center">
                        <p className={`text-xs ${
                          passwordStrength.score <= 2 ? 'text-red-400' : 
                          passwordStrength.score === 3 ? 'text-yellow-400' : 'text-green-400'
                        }`}>
                          {passwordStrength.label}
                        </p>
                      </div>
                      
                      <div className="mt-2 grid grid-cols-2 gap-1">
                        {[
                          { key: 'length', label: '8+ chars' },
                          { key: 'uppercase', label: 'Uppercase' },
                          { key: 'lowercase', label: 'Lowercase' },
                          { key: 'number', label: 'Number' },
                          { key: 'special', label: 'Special char' },
                        ].map(({ key, label }) => (
                          <div key={key} className="flex items-center gap-1 text-xs">
                            {passwordStrength.checks?.[key] ? (
                              <Check className="w-3 h-3 text-green-400" />
                            ) : (
                              <X className="w-3 h-3 text-gray-600" />
                            )}
                            <span className={passwordStrength.checks?.[key] ? 'text-green-400' : 'text-gray-600'}>
                              {label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {formErrors.password && <p className="text-red-400 text-xs mt-1">{formErrors.password}</p>}
                </div>

                {/* Terms Checkbox */}
                <div className="space-y-2">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="terms"
                      checked={agreedToTerms}
                      onCheckedChange={setAgreedToTerms}
                      className={`mt-0.5 ${formErrors.terms ? 'border-red-500' : ''}`}
                      data-testid="signup-terms-checkbox"
                    />
                    <label
                      htmlFor="terms"
                      className="text-sm leading-relaxed cursor-pointer text-gray-400"
                    >
                      I agree to the{' '}
                      <Link to="/terms" target="_blank" className="text-purple-400 hover:text-purple-300 underline">
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link to="/privacy" target="_blank" className="text-purple-400 hover:text-purple-300 underline">
                        Privacy Policy
                      </Link>
                    </label>
                  </div>
                  {formErrors.terms && <p className="text-red-400 text-xs ml-7">{formErrors.terms}</p>}
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  disabled={loading || (formData.password && passwordStrength.score < 3) || !agreedToTerms}
                  data-testid="signup-submit-button"
                  className="w-full py-6 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white font-medium shadow-lg shadow-purple-500/20 disabled:opacity-50"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {loading ? 'Creating account...' : 'Create Account'}
                </Button>
              </form>
            )}

            {/* Divider */}
            <div className="my-6 border-t border-white/[0.06]" />

            {/* Features */}
            <div className="space-y-2.5">
              {features.map((feature, i) => (
                <div key={i} className="flex items-center gap-2.5 text-sm text-gray-400">
                  <Check className="w-4 h-4 shrink-0 text-purple-400" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            {/* Terms Footer */}
            <p className="text-xs text-center mt-6 text-gray-600">
              By signing up, you agree to our{' '}
              <Link to="/terms" target="_blank" className="text-purple-400/80 hover:underline">
                Terms of Service
              </Link>
              {' '}and{' '}
              <Link to="/privacy" target="_blank" className="text-purple-400/80 hover:underline">
                Privacy Policy
              </Link>
            </p>
          </div>

          {/* Already have account */}
          <div className="text-center mt-6">
            <p className="text-gray-500">
              Already have an account?{' '}
              <button 
                onClick={() => navigate('/login')}
                className="text-purple-400 hover:text-purple-300 font-medium transition-colors"
                data-testid="signup-signin-link"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 px-6 border-t border-white/5">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between text-xs text-gray-600">
          <span>&copy; {new Date().getFullYear()} StackRanked.ai</span>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-gray-400 transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-gray-400 transition-colors">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SignupPage;
